import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import dayjs from 'dayjs';

// Fade timing constants (in milliseconds)
const FADE_START_MS = 60 * 1000;      // 1 minute - start fading
const FADE_END_MS = 62 * 1000;        // 1 minute + 2 seconds - remove from display

export default class EventTimingpointController extends Controller {
  @service eventData;
  @service watchlist;
  @service preferences;

  @tracked filterMode = 'all';  // 'all' or 'watchlist'
  @tracked showStatsSelector = false;

  // Map of record UUID -> timestamp used as the fade anchor. For historical
  // crossings this is the actual passing time from crossing.time, so a record
  // that happened 45 s before page-open will still have ~15 s of fade window.
  // Live crossings arriving in real time have crossing.time close to now, so
  // they get the full fade window like before.
  crossingRecordedTimes = new Map();

  // Timer ID for periodic fade updates
  _fadeTimerId = null;

  /**
   * Get the event data object
   */
  get eventDataObj() {
    return this.eventData.getEventData(this.model?.eventUuid);
  }

  /**
   * Check if we're viewing the Finish timing point
   */
  get isFinishMode() {
    return this.model?.timingPointUuid === 'finish';
  }

  /**
   * Get event precision for time formatting
   */
  get precision() {
    return this.eventDataObj?.event?.precision || 1000;
  }

  /**
   * Get the event name for display
   */
  get eventName() {
    return this.eventDataObj?.event?.name || '';
  }

  /**
   * Get timing point info from course elements
   */
  get timingPoint() {
    // Handle finish mode
    if (this.isFinishMode) {
      return {
        uuid: 'finish',
        name: 'Finish',
        description: 'Finish Line'
      };
    }

    const eventData = this.eventDataObj;
    if (!eventData?.courses) return null;

    for (const course of eventData.courses.values()) {
      if (!course.courseElements) continue;

      for (const el of course.courseElements) {
        if (el.intermediateTimingPointUuid === this.model?.timingPointUuid) {
          return {
            uuid: el.intermediateTimingPointUuid,
            name: el.intermediateTimingPointName,
            description: el.intermediateTimingPointDescription
          };
        }
      }
    }
    return null;
  }

  /**
   * Get display name for the timing point (description or name)
   */
  get timingPointDisplayName() {
    const tp = this.timingPoint;
    if (!tp) return '';
    return tp.description || tp.name || '';
  }

  /**
   * Page title
   */
  get pageTitle() {
    const tpName = this.timingPointDisplayName;
    const eventName = this.eventName;
    if (tpName && eventName) {
      return `${tpName} - ${eventName}`;
    }
    return tpName || eventName || 'Timing Point';
  }

  /**
   * Get course elements for an entry's course that use this timing point.
   * Returns array sorted by order.
   */
  getCourseElementsForEntry(eventData, entry) {
    const entryClass = eventData.classes.get(entry.classUuid);
    if (!entryClass) return [];

    const classGroup = eventData.classGroups.get(entryClass.classGroupUuid);
    if (!classGroup) return [];

    const course = eventData.courses.get(classGroup.courseUuid);
    if (!course?.courseElements) return [];

    const timingPointUuid = this.model?.timingPointUuid;

    // Get all course elements that use this timing point, sorted by order
    return course.courseElements
      .filter(ce => ce.intermediateTimingPointUuid === timingPointUuid)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Get all crossings for this timing point with computed stats.
   * Each crossing is matched to a course element based on crossing order.
   */
  get allCrossingsForTimingPoint() {
    // Access _dataVersion to establish tracking dependency for reactivity
    // eslint-disable-next-line no-unused-vars
    const version = this.eventData._dataVersion;

    // Handle finish mode
    if (this.isFinishMode) {
      return this.finishCrossings;
    }

    const eventData = this.eventDataObj;
    if (!eventData?.intermediateRecords) return [];

    const timingPointUuid = this.model?.timingPointUuid;
    if (!timingPointUuid) return [];

    const crossings = [];

    // Scan all entries' intermediate records
    for (const [entryUuid, records] of eventData.intermediateRecords.entries()) {
      // Get entry info
      const entry = eventData.entries.get(entryUuid);
      if (!entry) continue;

      // Get course elements for this entry's course that use this timing point
      const courseElements = this.getCourseElementsForEntry(eventData, entry);
      if (courseElements.length === 0) continue;

      // Get classgroup for this entry
      const entryClass = eventData.classes.get(entry.classUuid);
      const classGroupUuid = entryClass?.classGroupUuid || null;

      // Get start time
      const startRecord = eventData.startRecords.get(entryUuid);
      const startTime = startRecord?.startTime || startRecord?.assignedStartTime;

      // Get all iTRs for this timing point, sorted by time
      const iTRsForTimingPoint = records
        .filter(r => r.intermediateTimingPointUuid === timingPointUuid && r.time)
        .sort((a, b) => new Date(a.time) - new Date(b.time));

      // Match each iTR to a course element in order
      for (let i = 0; i < iTRsForTimingPoint.length && i < courseElements.length; i++) {
        const record = iTRsForTimingPoint[i];
        const courseElement = courseElements[i];

        // Calculate elapsed time if we have start time
        const elapsed = startTime ? dayjs(record.time).diff(dayjs(startTime)) : null;

        crossings.push({
          uuid: record.uuid,
          entryUuid,
          time: record.time,
          elapsed,
          hasStartTime: !!startTime,
          bib: entry.bib,
          name: [entry.firstName, entry.lastName].filter(Boolean).join(' '),
          classGroupUuid,
          courseElementUuid: courseElement.uuid,
          courseElementName: courseElement.name || `Split ${i + 1}`
        });
      }
    }

    // Sort by elapsed time (fastest first), entries without elapsed at end
    crossings.sort((a, b) => {
      if (a.elapsed !== null && b.elapsed !== null) {
        return a.elapsed - b.elapsed;
      }
      if (a.elapsed !== null) return -1;
      if (b.elapsed !== null) return 1;
      return new Date(a.time) - new Date(b.time);
    });

    console.log(`[TimingPoint] Found ${crossings.length} crossings for timing point ${timingPointUuid}`);

    return crossings;
  }

  /**
   * Get finish crossings for finish mode.
   * Returns all finish records formatted as crossings.
   */
  get finishCrossings() {
    const eventData = this.eventDataObj;
    if (!eventData?.finishRecords) return [];

    const crossings = [];

    for (const [entryUuid, finishRecord] of eventData.finishRecords) {
      // Skip records without finish time
      if (!finishRecord.finishTime) continue;

      const entry = eventData.entries.get(entryUuid);
      if (!entry) continue;

      // Get classgroup for this entry
      const entryClass = eventData.classes.get(entry.classUuid);
      const classGroupUuid = entryClass?.classGroupUuid || null;

      // Get start time
      const startRecord = eventData.startRecords.get(entryUuid);
      const startTime = startRecord?.startTime || startRecord?.assignedStartTime;

      // Calculate elapsed time if we have start time
      const elapsed = startTime ? dayjs(finishRecord.finishTime).diff(dayjs(startTime)) : null;

      crossings.push({
        uuid: finishRecord.uuid || `finish-${entryUuid}`,
        entryUuid,
        time: finishRecord.finishTime,
        elapsed,
        hasStartTime: !!startTime,
        bib: entry.bib,
        name: [entry.firstName, entry.lastName].filter(Boolean).join(' '),
        classGroupUuid,
        courseElementUuid: 'finish',
        courseElementName: 'Finish'
      });
    }

    // Sort by elapsed time (fastest first), entries without elapsed at end
    crossings.sort((a, b) => {
      if (a.elapsed !== null && b.elapsed !== null) {
        return a.elapsed - b.elapsed;
      }
      if (a.elapsed !== null) return -1;
      if (b.elapsed !== null) return 1;
      return new Date(a.time) - new Date(b.time);
    });

    console.log(`[TimingPoint] Found ${crossings.length} finish crossings`);

    return crossings;
  }

  /**
   * Get crossings visible within the fade window, with computed stats.
   * Place and time back are calculated within classgroup AND course element.
   */
  get displayedCrossings() {
    const allCrossings = this.allCrossingsForTimingPoint;
    const eventUuid = this.model?.eventUuid;
    const now = Date.now();

    // Anchor each crossing's fade timer to the actual passing time instead of
    // "when the browser first saw the record". A crossing that happened before
    // the page opened still shows for whatever fade window remains based on
    // its real timestamp; live crossings behave as before because their
    // crossing.time is effectively now.
    for (const crossing of allCrossings) {
      if (!this.crossingRecordedTimes.has(crossing.uuid)) {
        const crossingTimestamp = crossing.time ? new Date(crossing.time).getTime() : now;
        this.crossingRecordedTimes.set(crossing.uuid, crossingTimestamp);
      }
    }

    // Drop any tracked timestamps whose records no longer exist (deleted or
    // bib cleared) so the map doesn't grow unbounded.
    const currentUuids = new Set(allCrossings.map(c => c.uuid));
    for (const uuid of this.crossingRecordedTimes.keys()) {
      if (!currentUuids.has(uuid)) {
        this.crossingRecordedTimes.delete(uuid);
      }
    }

    // Filter out crossings older than the fade window
    const visibleCrossings = allCrossings.filter(crossing => {
      const recordedAt = this.crossingRecordedTimes.get(crossing.uuid) || now;
      return (now - recordedAt) < FADE_END_MS;
    });

    console.log(`[TimingPoint] displayedCrossings: ${allCrossings.length} total, ${visibleCrossings.length} visible within fade window`);

    // Pre-compute rankings by (classGroupUuid, courseElementUuid)
    const rankings = new Map();
    for (const crossing of allCrossings) {
      if (crossing.elapsed === null || !crossing.classGroupUuid || !crossing.courseElementUuid) continue;

      const rankKey = `${crossing.classGroupUuid}:${crossing.courseElementUuid}`;
      if (!rankings.has(rankKey)) {
        const rankedCrossings = allCrossings
          .filter(c =>
            c.classGroupUuid === crossing.classGroupUuid &&
            c.courseElementUuid === crossing.courseElementUuid &&
            c.elapsed !== null
          )
          .sort((a, b) => a.elapsed - b.elapsed);
        rankings.set(rankKey, rankedCrossings);
      }
    }

    // Pre-compute overall rankings by courseElementUuid (all classgroups combined)
    const overallRankings = new Map();
    for (const crossing of allCrossings) {
      if (crossing.elapsed === null || !crossing.courseElementUuid) continue;

      const ceKey = crossing.courseElementUuid;
      if (!overallRankings.has(ceKey)) {
        const rankedCrossings = allCrossings
          .filter(c => c.courseElementUuid === ceKey && c.elapsed !== null)
          .sort((a, b) => a.elapsed - b.elapsed);
        overallRankings.set(ceKey, rankedCrossings);
      }
    }

    // Pre-compute class-only rankings (by classGroupUuid across all course elements)
    const classRankings = new Map();
    for (const crossing of allCrossings) {
      if (crossing.elapsed === null || !crossing.classGroupUuid) continue;

      const cgKey = crossing.classGroupUuid;
      if (!classRankings.has(cgKey)) {
        const rankedCrossings = allCrossings
          .filter(c => c.classGroupUuid === cgKey && c.elapsed !== null)
          .sort((a, b) => a.elapsed - b.elapsed);
        classRankings.set(cgKey, rankedCrossings);
      }
    }

    // Build crossing objects (sorted by arrival time, newest first)
    const crossings = visibleCrossings
      .sort((a, b) => {
        const aTime = this.crossingRecordedTimes.get(a.uuid) || 0;
        const bTime = this.crossingRecordedTimes.get(b.uuid) || 0;
        return bTime - aTime;
      })
      .map(crossingData => {
        let place = null;
        let timeBack = null;
        let gap = null;
        let timeOutTop10 = null;
        let timeOutTop30 = null;
        let timeGain10 = null;
        let classPlace = null;
        let classTimeBack = null;

        if (crossingData.elapsed !== null && crossingData.classGroupUuid && crossingData.courseElementUuid) {
          const rankKey = `${crossingData.classGroupUuid}:${crossingData.courseElementUuid}`;
          const rankedCrossings = rankings.get(rankKey) || [];
          const placeIndex = rankedCrossings.findIndex(c => c.uuid === crossingData.uuid);
          if (placeIndex >= 0) {
            place = placeIndex + 1;
            const leaderElapsed = rankedCrossings[0]?.elapsed;
            if (leaderElapsed !== null) {
              timeBack = crossingData.elapsed - leaderElapsed;
            }
            // Calculate gap to person ahead
            if (placeIndex > 0) {
              const prevElapsed = rankedCrossings[placeIndex - 1]?.elapsed;
              if (prevElapsed !== null) {
                gap = crossingData.elapsed - prevElapsed;
              }
            }
          }

          // Calculate overall rankings (all classgroups combined)
          const overallRanked = overallRankings.get(crossingData.courseElementUuid) || [];
          const overallPlaceIndex = overallRanked.findIndex(c => c.uuid === crossingData.uuid);
          const overallPlace = overallPlaceIndex >= 0 ? overallPlaceIndex + 1 : null;

          // Time out from top 10
          if (overallPlace !== null && overallPlace > 10 && overallRanked.length >= 10) {
            timeOutTop10 = crossingData.elapsed - overallRanked[9].elapsed;
          }

          // Time out from top 30
          if (overallPlace !== null && overallPlace > 30 && overallRanked.length >= 30) {
            timeOutTop30 = crossingData.elapsed - overallRanked[29].elapsed;
          }

          // Time to gain 10 positions
          if (overallPlace !== null && overallPlace > 10) {
            const targetIndex = overallPlace - 11; // 0-indexed, move up 10 spots
            if (targetIndex >= 0 && overallRanked[targetIndex]) {
              timeGain10 = crossingData.elapsed - overallRanked[targetIndex].elapsed;
            }
          }

          // Class place and time from class leader
          const classRanked = classRankings.get(crossingData.classGroupUuid) || [];
          if (classRanked.length > 0) {
            const classPlaceIndex = classRanked.findIndex(c => c.uuid === crossingData.uuid);
            if (classPlaceIndex >= 0) {
              classPlace = classPlaceIndex + 1;
            }
            if (classRanked[0].elapsed !== null) {
              classTimeBack = crossingData.elapsed - classRanked[0].elapsed;
            }
          }
        }

        const recordedAt = this.crossingRecordedTimes.get(crossingData.uuid) || now;
        const age = now - recordedAt;
        const isFading = age > FADE_START_MS;

        return {
          uuid: crossingData.uuid,
          entryUuid: crossingData.entryUuid,
          bib: crossingData.bib,
          name: crossingData.name,
          courseElementName: crossingData.courseElementName,
          elapsed: crossingData.elapsed,
          elapsedFormatted: crossingData.elapsed !== null
            ? this.formatTime(crossingData.elapsed)
            : 'No start',
          hasStartTime: crossingData.hasStartTime,
          place,
          timeBack,
          timeBackFormatted: timeBack > 0 ? this.formatTime(timeBack) : '',
          gap,
          gapFormatted: gap > 0 ? this.formatTime(gap) : '',
          timeOutTop10,
          timeOutTop10Formatted: timeOutTop10 > 0 ? this.formatTime(timeOutTop10) : '',
          timeOutTop30,
          timeOutTop30Formatted: timeOutTop30 > 0 ? this.formatTime(timeOutTop30) : '',
          timeGain10,
          timeGain10Formatted: timeGain10 > 0 ? this.formatTime(timeGain10) : '',
          classPlace,
          classTimeBack,
          classTimeBackFormatted: classTimeBack > 0 ? this.formatTime(classTimeBack) : '',
          isWatched: this.watchlist.isWatched(eventUuid, crossingData.entryUuid),
          isFading
        };
      });

    return crossings;
  }

  /**
   * Get filtered crossings based on filter mode
   */
  get filteredCrossings() {
    if (this.filterMode === 'watchlist') {
      const eventUuid = this.model?.eventUuid;
      return this.displayedCrossings.filter(c =>
        this.watchlist.isWatched(eventUuid, c.entryUuid)
      );
    }
    return this.displayedCrossings;
  }

  /**
   * Format time based on event precision
   */
  formatTime(milliseconds) {
    if (milliseconds === null || milliseconds === undefined) {
      return '';
    }

    const precision = this.precision;
    const isNegative = milliseconds < 0;
    const absMilliseconds = Math.abs(milliseconds);

    const hours = Math.floor(absMilliseconds / 3600000);
    const minutes = Math.floor((absMilliseconds % 3600000) / 60000);
    const seconds = Math.floor((absMilliseconds % 60000) / 1000);
    const ms = absMilliseconds % 1000;

    let formatted;
    if (hours > 0) {
      formatted = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      formatted = `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    switch (precision) {
      case 100:
        formatted += `.${Math.floor(ms / 100)}`;
        break;
      case 10:
        formatted += `.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
        break;
      case 1:
        formatted += `.${String(ms).padStart(3, '0')}`;
        break;
    }

    return isNegative ? `-${formatted}` : formatted;
  }

  @action
  setFilter(mode) {
    this.filterMode = mode;
  }

  @action
  toggleStatsSelector() {
    this.showStatsSelector = !this.showStatsSelector;
  }

  /**
   * Get visible stats configuration from preferences
   */
  get visibleStats() {
    const config = this.preferences.getCrossingStatsConfig();
    return {
      place: config.includes('place'),
      elapsed: config.includes('elapsed'),
      timeBack: config.includes('timeBack'),
      gap: config.includes('gap'),
      timeOutTop10: config.includes('timeOutTop10'),
      timeOutTop30: config.includes('timeOutTop30'),
      timeGain10: config.includes('timeGain10'),
      classPlace: config.includes('classPlace'),
      classTimeBack: config.includes('classTimeBack')
    };
  }

  /**
   * Get available stats for selector
   */
  get availableStats() {
    return this.preferences.getAvailableCrossingStats();
  }

  /**
   * Reset state when entering route.
   */
  resetState() {
    this.crossingRecordedTimes.clear();
    this.filterMode = 'all';
    this.startFadeTimer();
    console.log(`[TimingPoint] resetState called`);
  }

  /**
   * Start the fade timer to periodically trigger re-renders.
   * This ensures cards fade out and disappear on time.
   */
  startFadeTimer() {
    this.stopFadeTimer();
    // Update every 2 seconds to check for fade/removal
    this._fadeTimerId = setInterval(() => {
      // Increment data version to trigger getter re-evaluation
      this.eventData._dataVersion++;
    }, 2000);
    console.log(`[TimingPoint] Fade timer started`);
  }

  /**
   * Stop the fade timer.
   */
  stopFadeTimer() {
    if (this._fadeTimerId) {
      clearInterval(this._fadeTimerId);
      this._fadeTimerId = null;
      console.log(`[TimingPoint] Fade timer stopped`);
    }
  }
}
